<template>
  <transition name="modal">
    <div
      v-if="visible"
      class="fixed inset-0 z-[300] hbox items-center justify-center p-4"
      data-testid="base-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
    >
      <div class="absolute inset-0 bg-[#0b1b2e]/50" data-testid="base-modal-overlay" @click="onOverlay"></div>
      <div
        ref="panel"
        tabindex="-1"
        class="relative max-h-[calc(100dvh-2rem)] w-full overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_24px_48px_rgba(16,42,67,0.18)]"
        :class="maxWidth"
      >
        <div class="hbox mb-4 items-center justify-between">
          <h3 :id="titleId" class="text-[15px] font-bold tracking-tight text-[#0b1b2e]">{{ title }}</h3>
          <button
            type="button"
            class="hbox h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[#607187] transition-colors hover:bg-[#f3f5f8] hover:text-[#0b1b2e] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#7ab9f2]"
            data-testid="base-modal-close"
            aria-label="Cerrar"
            @click="$emit('close')"
          >
            <AppIcon name="x-mark" size="h-4 w-4" />
          </button>
        </div>
        <div class="mb-5">
          <slot></slot>
        </div>
        <div v-if="$slots.footer" class="hbox justify-end gap-2">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/*
 * BaseModal — modal ligero con Tailwind (patrón de reemplazo).
 *
 * Sustituye a los diálogos de Vuetify y a los confirm/alert de sweetalert2
 * en las piezas migradas. Sin dependencias: transición CSS, cierre con ESC,
 * clic en overlay y slot de acciones.
 *
 * Props:
 * - maxWidth: ancho del panel (clase Tailwind). Default 'max-w-sm'.
 * - persistent: si es true, ni el overlay ni ESC cierran (formularios).
 *   El botón X siempre cierra.
 * El panel usa toda la altura disponible menos el margen del overlay (p-4),
 * para que los formularios no hagan scroll en laptops de 720px.
 */
import AppIcon from './AppIcon.vue'

export default {
  name: 'BaseModal',
  components: { AppIcon },
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '' },
    maxWidth: { type: String, default: 'max-w-sm' },
    persistent: { type: Boolean, default: false },
  },
  computed: {
    titleId() { return 'base-modal-title-' + this._uid },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.previousFocus = document.activeElement
          document.addEventListener('keydown', this.onKeydown)
          this.$nextTick(() => {
            if (!this.visible || !this.$refs.panel) return
            const fields = this.focusableControls()
            const first = fields.find(el => el.matches('input:not([role="combobox"]),select,textarea')) || this.$refs.panel
            first.focus()
          })
        } else {
          document.removeEventListener('keydown', this.onKeydown)
          this.restoreFocus()
        }
      },
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    this.restoreFocus()
  },
  methods: {
    restoreFocus() {
      if (this.previousFocus && this.previousFocus.isConnected) this.previousFocus.focus()
      this.previousFocus = null
    },
    focusableControls() {
      if (!this.$refs.panel) return []
      return Array.from(this.$refs.panel.querySelectorAll('button,input,select,textarea,a[href],[tabindex]')).filter(el => {
        if (el.disabled || el.tabIndex < 0) return false
        for (let parent = el; parent && parent !== this.$refs.panel; parent = parent.parentElement) {
          const style = getComputedStyle(parent)
          if (parent.hidden || style.display === 'none' || style.visibility === 'hidden') return false
        }
        return true
      })
    },
    onKeydown(event) {
      // Un modal superpuesto controla el teclado, sin cerrar el de abajo.
      const dialogs = document.querySelectorAll('[data-testid="base-modal"]')
      const ownDialog = this.$refs.panel && this.$refs.panel.closest('[data-testid="base-modal"]')
      if (dialogs.length && dialogs[dialogs.length - 1] !== ownDialog) return
      if (!this.$refs.panel) return
      if (event.defaultPrevented) return
      if (event.key === 'Tab') {
        const controls = this.focusableControls()
        const first = controls[0] || this.$refs.panel
        const last = controls[controls.length - 1] || first
        const active = document.activeElement
        if (!controls.length || !this.$refs.panel.contains(active) ||
          (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
          event.preventDefault()
          ;(event.shiftKey ? last : first).focus()
        }
      }
      if (event.key === 'Escape' && !this.persistent) this.$emit('close')
    },
    onOverlay() {
      if (!this.persistent) this.$emit('close')
    },
  },
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.18s ease;
}
.modal-enter,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-active > div:last-child,
.modal-leave-active > div:last-child {
  transition: transform 0.18s ease;
}
.modal-enter > div:last-child,
.modal-leave-to > div:last-child {
  transform: scale(0.96) translateY(8px);
}
</style>
